import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ProductType } from "@/shared/types/enums";
import { contractAdminService } from "@/features/contracts/services/contract-admin.service";
import { clientAdminService } from "@/features/clients/services/client-admin.service";
import { sellerService } from "@/features/sellers/services/seller.service";
import { stockAdminService } from "@/features/stocks/services/stock-admin.service";
import { productService } from "@/features/products/services/product-api.service";
import { tagAdminService } from "@/features/tags/services/tags-admin.service";
import type { SellerResponseDTO } from "@/features/sellers/types/seller.types";
import type { ProductResponseDTO } from "@/features/products/types/product.types";
import type { TagResponseDTO } from "@/features/tags/types";
import type { StockResponseDTO } from "@/features/stocks/types/stock.types";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import type { NewClientData } from "../schemas";
import type {
  ContractOption,
  ClientOption,
} from "../components/wizard/ContractSelector";

const AUX_PAGE_SIZE = 200;
const SEARCH_PAGE_SIZE = 10;

/**
 * Buscas de contrato, cliente e peça no escopo admin.
 *
 * Separado das listas auxiliares de propósito: a página de criação
 * (`/alugueis/novo-admin`) só precisa destas buscas, porque as etapas
 * compartilhadas com o fluxo do vendedor carregam vendedores, produtos e tags
 * por conta própria. Quem precisa das listas usa `useRentalWizardLists`.
 */
export function useRentalWizardSearch() {
  const searchContracts = useCallback(
    async (term: string, signal?: AbortSignal): Promise<ContractOption[]> => {
      const trimmed = term.trim();
      const map = new Map<number, ContractOption>();
      const byNumber = await contractAdminService.search(
        {
          contractNumber: trimmed,
          size: SEARCH_PAGE_SIZE,
        },
        { signal },
      );
      byNumber.content.forEach((c) =>
        map.set(c.id, {
          id: c.id,
          contractNumber: c.contractNumber,
          clientName: c.clientName,
          clientId: c.clientId,
        }),
      );
      if (/^\d+$/.test(trimmed)) {
        try {
          const byId = await contractAdminService.getById(Number(trimmed), {
            signal,
          });
          if (!map.has(byId.id))
            map.set(byId.id, {
              id: byId.id,
              contractNumber: byId.contractNumber,
              clientName: byId.clientName,
              clientId: byId.clientId,
            });
        } catch {
          /* not found */
        }
      }
      return Array.from(map.values());
    },
    [],
  );

  const searchClients = useCallback(
    async (term: string, signal?: AbortSignal): Promise<ClientOption[]> => {
      const res = await clientAdminService.search(
        { name: term, size: SEARCH_PAGE_SIZE },
        { signal },
      );
      return res.content.map((c) => ({
        id: c.id,
        name: c.name,
        document: c.document,
      }));
    },
    [],
  );

  const createClient = useCallback(
    async (data: NewClientData): Promise<{ id: number; name: string }> => {
      const client = await clientAdminService.create(data);
      return { id: client.id, name: client.name };
    },
    [],
  );

  const createContract = useCallback(
    async (
      contractNumber: string,
      clientId: number,
    ): Promise<{ id: number; contractNumber: string }> => {
      const contract = await contractAdminService.create({
        contractNumber,
        clientId,
      });
      return { id: contract.id, contractNumber: contract.contractNumber };
    },
    [],
  );

  const searchStocks = useCallback(
    async (term: string, signal?: AbortSignal): Promise<StockResponseDTO[]> => {
      const trimmed = term.trim();
      if (/^\d+$/.test(trimmed)) {
        try {
          const stock = await stockAdminService.getById(Number(trimmed), {
            signal,
          });
          return [stock];
        } catch {
          return [];
        }
      }
      const res = await stockAdminService.search(
        { barcode: [trimmed], pageSize: SEARCH_PAGE_SIZE },
        { signal },
      );
      return res.content;
    },
    [],
  );

  return {
    searchContracts,
    searchClients,
    createClient,
    createContract,
    searchStocks,
  };
}

/**
 * Listas auxiliares do dialog de EDIÇÃO de aluguel (`RentalWizard`), que tem
 * seus próprios selects em vez dos campos compartilhados do fluxo do vendedor.
 *
 * A `AlugueisPage` também usa `tags` para o mapa de tags da tabela.
 */
export function useRentalWizardLists() {
  const [sellers, setSellers] = useState<SellerResponseDTO[]>([]);
  const [gownProducts, setGownProducts] = useState<ProductResponseDTO[]>([]);
  const [tags, setTags] = useState<TagResponseDTO[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      sellerService.list(
        { size: AUX_PAGE_SIZE },
        { signal: controller.signal },
      ),
      productService.list(
        { type: [ProductType.GOWN], size: AUX_PAGE_SIZE },
        { signal: controller.signal },
      ),
      tagAdminService.search(
        { size: AUX_PAGE_SIZE },
        { signal: controller.signal },
      ),
    ])
      .then(([sellersRes, productsRes, tagsRes]) => {
        if (controller.signal.aborted) return;
        setSellers(sellersRes.content);
        setGownProducts(productsRes.content);
        setTags(tagsRes.content);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        toast.error(
          getApiErrorMessage(error, "Erro ao carregar dados auxiliares"),
        );
      });
    return () => controller.abort();
  }, []);

  return { sellers, gownProducts, tags };
}
